import { useState } from "react";
import { View, Text, Pressable } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  interpolateColor,
} from "react-native-reanimated";
import { ArrowLeftRight, MapPin } from "lucide-react-native";
import { useNavigation } from "@react-navigation/native";
import { EMBER } from "../../../onboarding/theme";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import StatusBadge from "../../components/StatusBadge";
import { useRegion, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

function PlaceholderClinicRow({ widthPct }: { widthPct: number }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        padding: 12,
        borderRadius: 10,
        backgroundColor: "rgba(255,255,255,0.12)",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.15)",
      }}
    >
      <View
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          backgroundColor: "rgba(255,255,255,0.15)",
        }}
      />
      <View style={{ flex: 1, gap: 6 }}>
        <View
          style={{
            width: `${widthPct}%`,
            height: 10,
            borderRadius: 4,
            backgroundColor: "rgba(42,37,34,0.08)",
          }}
        />
        <View
          style={{
            width: "40%",
            height: 8,
            borderRadius: 4,
            backgroundColor: "rgba(42,37,34,0.05)",
          }}
        />
      </View>
      <View
        style={{
          width: 50,
          height: 10,
          borderRadius: 4,
          backgroundColor: "rgba(42,37,34,0.06)",
        }}
      />
    </View>
  );
}

const BUTTON_RADIUS = 14;

function ShimmerButton({ onPress }: { onPress: () => void }) {
  const capturePress = usePressCapture("ShimmerButton");
  const shimmer = useSharedValue(0);

  useState(() => {
    shimmer.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 2000 }),
        withTiming(0, { duration: 2000 }),
      ),
      -1,
      false,
    );
  });

  const borderStyle = useAnimatedStyle(() => ({
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: BUTTON_RADIUS,
    borderWidth: 1,
    borderColor: interpolateColor(
      shimmer.value,
      [0, 0.5, 1],
      [
        "rgba(255, 255, 255, 0.2)",
        "rgba(255, 255, 255, 0.6)",
        "rgba(255, 255, 255, 0.2)",
      ],
    ),
  }));

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel("Find Care"));
        onPress();
      }}
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        height: 44,
        paddingHorizontal: 16,
        borderRadius: BUTTON_RADIUS,
        backgroundColor: "rgba(255,255,255,0.5)",
      }}
    >
      <Animated.View style={borderStyle} />
      <MapPin size={16} color={EMBER.colors.text} strokeWidth={1.5} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          fontWeight: "600",
          color: EMBER.colors.text,
        }}
      >
        Find Care
      </Text>
    </Pressable>
  );
}

export default function CompareClinicCostsCard() {
  const navigation = useNavigation<{
    navigate: (screen: string, params?: object) => void;
  }>();
  const region = useRegion();
  const t = useTranslations();
  const isUS = region === "US";

  return (
    <View style={{ ...GLASS_CARD_STYLE, padding: 20 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <ArrowLeftRight
            size={18}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "600",
              color: EMBER.colors.text,
            }}
          >
            {t("compare_clinic.compare_clinic_costs")}
          </Text>
        </View>
        <StatusBadge type="coming-soon" />
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: EMBER.colors.textLight,
          lineHeight: 19,
          marginBottom: 14,
        }}
      >
        {t("compare_clinic.comparison_coming_soon")}
      </Text>

      {/* Placeholder clinic rows */}
      <View
        style={{
          gap: 8,
          opacity: 0.5,
          marginBottom: isUS ? 16 : 0,
        }}
      >
        <PlaceholderClinicRow widthPct={65} />
        <PlaceholderClinicRow widthPct={55} />
        <PlaceholderClinicRow widthPct={70} />
      </View>

      {/* Find Care CTA — US only; FindCare uses US clinic data */}
      {isUS && (
        <ShimmerButton
          onPress={() => {
            navigation.navigate("Access", {
              screen: "FindCare",
              params: { returnTab: "Costs" },
            });
          }}
        />
      )}
    </View>
  );
}
